import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import { all_routes } from "../../router/all_routes"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import {
  getProfile,
  updateProfile,
  changePassword,
  uploadProfilePicture,
  UserProfile,
  UpdateProfileData,
  ChangePasswordData,
} from "../../../service/profile"

type PasswordField = "currentPassword" | "newPassword" | "confirmPassword"

interface PasswordErrors {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

const Profile = () => {
  const route = all_routes
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
  })

  const [addressInfo, setAddressInfo] = useState({
    city: "",
    area: "",
    buildingName: "",
    blockNo: "",
    houseNo: "",
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })

  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  })

  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({})
  const [isPasswordValid, setIsPasswordValid] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const profileData = await getProfile()
      setProfile(profileData)
      setPersonalInfo({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
      })

      setAddressInfo({
        city: profileData.address?.city || "",
        area: profileData.address?.area || "",
        buildingName: profileData.address?.buildingName || "",
        blockNo: profileData.address?.blockNo || "",
        houseNo: profileData.address?.houseNo || "",
      })
    } catch (err) {
      console.error("Error fetching profile:", err)
      toast.error("Failed to fetch profile data")
    } finally {
      setLoading(false)
    }
  }

  // Password validation function
  const validatePassword = (field: keyof PasswordErrors, value: string) => {
    const errors: PasswordErrors = { ...passwordErrors }

    switch (field) {
      case "currentPassword":
        if (!value.trim()) {
          errors.currentPassword = "Current password is required"
        } else {
          delete errors.currentPassword
        }
        break

      case "newPassword":
        if (!value.trim()) {
          errors.newPassword = "New password is required"
        } else if (value.length < 6) {
          errors.newPassword = "Password must be at least 6 characters long"
        } else if (!/(?=.*[a-z])/.test(value)) {
          errors.newPassword =
            "Password must contain at least one lowercase letter"
        } else if (!/(?=.*[A-Z])/.test(value)) {
          errors.newPassword =
            "Password must contain at least one uppercase letter"
        } else if (!/(?=.*\d)/.test(value)) {
          errors.newPassword = "Password must contain at least one number"
        } else if (!/(?=.*[@$!%*?&])/.test(value)) {
          errors.newPassword =
            "Password must contain at least one special character (@$!%*?&)"
        } else {
          delete errors.newPassword
        }

        // Validate confirm password if it has value
        if (
          passwordData.confirmPassword &&
          value !== passwordData.confirmPassword
        ) {
          errors.confirmPassword = "Passwords do not match"
        } else if (
          passwordData.confirmPassword &&
          value === passwordData.confirmPassword
        ) {
          delete errors.confirmPassword
        }
        break

      case "confirmPassword":
        if (!value.trim()) {
          errors.confirmPassword = "Please confirm your password"
        } else if (value !== passwordData.newPassword) {
          errors.confirmPassword = "Passwords do not match"
        } else {
          delete errors.confirmPassword
        }
        break
    }

    setPasswordErrors(errors)

    // Check if all fields are valid
    const isValid =
      passwordData.currentPassword.trim() !== "" &&
      passwordData.newPassword.trim() !== "" &&
      passwordData.confirmPassword.trim() !== "" &&
      Object.keys(errors).length === 0

    setIsPasswordValid(isValid)
  }

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility(prevState => ({
      ...prevState,
      [field]: !prevState[field],
    }))
  }

  const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleAddressInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setAddressInfo(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }))

    // Validate the changed field
    validatePassword(name as keyof PasswordErrors, value)
  }

  const resetPersonalInfoForm = () => {
    if (profile) {
      setPersonalInfo({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber,
      })
    }
  }

  const resetAddressInfoForm = () => {
    if (profile) {
      setAddressInfo({
        city: profile.address?.city || "",
        area: profile.address?.area || "",
        buildingName: profile.address?.buildingName || "",
        blockNo: profile.address?.blockNo || "",
        houseNo: profile.address?.houseNo || "",
      })
    }
  }

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    })
    setPasswordErrors({})
    setIsPasswordValid(false)
  }

  // Handle profile picture upload
  const handleProfilePictureUpload = async (file: File) => {
    if (!file) return

    // Validate file type
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      toast.error("Please upload a valid image file (JPG, PNG)")
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB")
      return
    }

    try {
      setUploadingPhoto(true)
      const updatedProfile = await uploadProfilePicture(file)
      setProfile(updatedProfile)
      await fetchProfile()
      toast.success("Profile picture updated successfully!")
    } catch (err: any) {
      console.error("Error uploading profile picture:", err)
      toast.error(
        err.response?.data?.message || "Failed to upload profile picture"
      )
    } finally {
      setUploadingPhoto(false)
    }
  }

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleProfilePictureUpload(file)
    }
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Handle drag and drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      handleProfilePictureUpload(file)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleUpdatePersonalInfo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (updating) return

    try {
      setUpdating(true)

      const updateData: UpdateProfileData = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        phoneNumber: personalInfo.phoneNumber,
      }

      const updatedProfile = await updateProfile(updateData)
      setProfile(updatedProfile)
      toast.success("Personal information updated successfully!")
      // Close modal
      const el = document.getElementById("edit_personal_information")
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal")
        el.click()
        el.removeAttribute("data-bs-dismiss")
      }
    } catch (err: any) {
      console.error("Error updating personal info:", err)
      toast.error(
        err.response?.data?.message || "Failed to update personal information"
      )
    } finally {
      setUpdating(false)
    }
  }

  const handleUpdateAddressInfo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (updating) return

    try {
      setUpdating(true)

      const updateData: UpdateProfileData = {
        address: addressInfo,
      }

      const updatedProfile = await updateProfile(updateData)
      setProfile(updatedProfile)
      toast.success("Address information updated successfully!")
      const el = document.getElementById("edit_address_information")
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal")
        el.click()
        el.removeAttribute("data-bs-dismiss")
      }
    } catch (err: any) {
      console.error("Error updating address info:", err)
      toast.error(
        err.response?.data?.message || "Failed to update address information"
      )
    } finally {
      setUpdating(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Prevent multiple submissions
    if (updating) {
      return
    }

    // Basic validation
    const errors: PasswordErrors = {}

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required"
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = "New password is required"
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters"
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your password"
    } else if (passwordData.confirmPassword !== passwordData.newPassword) {
      errors.confirmPassword = "Passwords do not match"
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors)
      toast.error("Please fix the errors")
      return
    }

    try {
      setUpdating(true)

      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })

      toast.success("Password updated successfully!")
      resetPasswordForm()

      // Close the modal using the hidden button method
      const closeBtn = document.getElementById(
        "change_password_modal_close"
      ) as HTMLElement
      if (closeBtn) {
        closeBtn.click()
      }
    } catch (err: any) {
      console.error("Password Change Failed:", err)

      // Handle specific backend errors
      if (err.message?.includes("Current password is incorrect")) {
        setPasswordErrors({
          currentPassword: "Current password is incorrect",
        })
        toast.error("Current password is incorrect")
      } else if (err.message?.includes("must be different")) {
        setPasswordErrors({
          newPassword: "New password must be different from current password",
        })
        toast.error("New password must be different from current password")
      } else {
        toast.error(err.message || "Error changing password")
      }
    } finally {
      setUpdating(false)
    }
  }

  if (!profile) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning d-flex justify-content-between align-items-center">
            <span>No profile data found</span>
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={fetchProfile}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Profile</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Settings</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Profile
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="pe-1 mb-2">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-top">Refresh</Tooltip>}
                >
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white btn-icon me-1"
                    onClick={e => {
                      e.preventDefault()
                      fetchProfile()
                    }}
                  >
                    <i className="ti ti-refresh" />
                  </Link>
                </OverlayTrigger>
              </div>
            </div>
          </div>
          <div className="d-md-flex d-block mt-3">
            <div className="settings-right-sidebar me-md-3 border-0">
              <div className="card">
                <div className="card-header">
                  <h5>Personal Information</h5>
                </div>
                <div className="card-body">
                  <div className="settings-profile-upload">
                    <div className="d-flex align-items-center">
                      <div className="avatar avatar-xl me-3">
                        {profile.profilePicture ? (
                          <img
                            src={profile.profilePicture}
                            alt="Profile"
                            className="img-fluid rounded-circle"
                          />
                        ) : (
                          <ImageWithBasePath
                            src="assets/img/profiles/avatar-27.jpg"
                            alt="Profile"
                            className="img-fluid rounded-circle"
                          />
                        )}
                      </div>
                      <div className="title-upload">
                        <h5 className="mb-1">Edit Your Photo</h5>
                        <Link
                          to="#"
                          className="text-primary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Update
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    className="profile-uploader profile-uploader-two mb-0 mt-3"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <span className="upload-icon">
                      <i className="ti ti-upload" />
                    </span>
                    <div className="drag-upload-btn bg-transparent me-0 border-0">
                      <p className="upload-btn">
                        <span>Click to Upload</span> or drag and drop
                      </p>
                      <h6>JPG or PNG</h6>
                      <h6>(Max 5MB)</h6>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={handleFileInputChange}
                      disabled={uploadingPhoto}
                      style={{ display: "none" }}
                    />
                    {uploadingPhoto && (
                      <div className="mt-2 text-center">
                        <div
                          className="spinner-border spinner-border-sm text-primary me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Uploading...</span>
                        </div>
                        <span className="text-muted">Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-fill ps-0 border-0">
              <form>
                <div className="d-md-flex">
                  <div className="flex-fill">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5>Personal Information</h5>
                        <Link
                          to="#"
                          className="btn btn-primary btn-sm"
                          data-bs-toggle="modal"
                          data-bs-target="#edit_personal_information"
                        >
                          <i className="ti ti-edit me-2" />
                          Edit
                        </Link>
                      </div>
                      <div className="card-body pb-0">
                        <div className="d-block d-xl-flex">
                          <div className="mb-3 flex-fill me-xl-3 me-0">
                            <label className="form-label">First Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter First Name"
                              value={profile.firstName}
                              readOnly
                            />
                          </div>
                          <div className="mb-3 flex-fill">
                            <label className="form-label">Last Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Last Name"
                              value={profile.lastName}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Email Address</label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Enter Email"
                            value={profile.email}
                            readOnly
                          />
                        </div>
                        <div className="d-block d-xl-flex">
                          <div className="mb-3 flex-fill me-xl-3 me-0">
                            <label className="form-label">Phone Number</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Phone Number"
                              value={profile.phoneNumber}
                              readOnly
                            />
                          </div>
                          <div className="mb-3 flex-fill">
                            <label className="form-label">Status</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Status"
                              value={profile.status}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5>Address Information</h5>
                        <Link
                          to="#"
                          className="btn btn-primary btn-sm"
                          data-bs-toggle="modal"
                          data-bs-target="#edit_address_information"
                        >
                          <i className="ti ti-edit me-2" />
                          Edit
                        </Link>
                      </div>
                      <div className="card-body pb-0">
                        <div className="d-block d-xl-flex">
                          <div className="mb-3 flex-fill me-xl-3 me-0">
                            <label className="form-label">City</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter City"
                              value={profile.address?.city || ""}
                              readOnly
                            />
                          </div>
                          <div className="mb-3 flex-fill">
                            <label className="form-label">Area</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Area"
                              value={profile.address?.area || ""}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="d-block d-xl-flex">
                          <div className="mb-3 flex-fill me-xl-3 me-0">
                            <label className="form-label">Building Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Building Name"
                              value={profile.address?.buildingName || ""}
                              readOnly
                            />
                          </div>
                          <div className="mb-3 flex-fill">
                            <label className="form-label">Block No</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Block No"
                              value={profile.address?.blockNo || ""}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">House No</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter House No"
                            value={profile.address?.houseNo || ""}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5>Password</h5>
                        <Link
                          to="#"
                          className="btn btn-primary btn-sm"
                          data-bs-toggle="modal"
                          data-bs-target="#change_password"
                        >
                          Change
                        </Link>
                      </div>
                      <div className="card-body pb-0">
                        <div className="mb-3 mb-3">
                          <label className="form-label">Current Password</label>
                          <div className="pass-group d-flex">
                            <input
                              type={
                                passwordVisibility.currentPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                              placeholder="Enter current password"
                              readOnly
                            />
                            <span
                              className={`ti toggle-passwords ${
                                passwordVisibility.currentPassword
                                  ? "ti-eye"
                                  : "ti-eye-off"
                              }`}
                              onClick={() =>
                                togglePasswordVisibility("currentPassword")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Edit Profile Modal */}
      <div className="modal fade" id="edit_personal_information">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Personal Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetPersonalInfoForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdatePersonalInfo}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter First Name"
                        name="firstName"
                        value={personalInfo.firstName}
                        onChange={handlePersonalInfoChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Last Name"
                        name="lastName"
                        value={personalInfo.lastName}
                        onChange={handlePersonalInfoChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Phone Number"
                        name="phoneNumber"
                        value={personalInfo.phoneNumber}
                        onChange={handlePersonalInfoChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetPersonalInfoForm}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                  id="modal_confirm_personal"
                >
                  {updating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Profile Modal */}

      {/* Edit Address Modal */}
      <div className="modal fade" id="edit_address_information">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Address Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetAddressInfoForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateAddressInfo}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter City"
                        name="city"
                        value={addressInfo.city}
                        onChange={handleAddressInfoChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Area</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Area"
                        name="area"
                        value={addressInfo.area}
                        onChange={handleAddressInfoChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Building Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Building Name"
                        name="buildingName"
                        value={addressInfo.buildingName}
                        onChange={handleAddressInfoChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Block No</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Block No"
                        name="blockNo"
                        value={addressInfo.blockNo}
                        onChange={handleAddressInfoChange}
                      />
                    </div>
                    <div className="mb-0">
                      <label className="form-label">House No</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter House No"
                        name="houseNo"
                        value={addressInfo.houseNo}
                        onChange={handleAddressInfoChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetAddressInfoForm}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                  id="modal_confirm_address"
                >
                  {updating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Address Modal */}

      {/* Change Password Modal */}
      <div className="modal fade" id="change_password">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Change Password</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetPasswordForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Current Password</label>
                      <div className="pass-group d-flex">
                        <input
                          type={
                            passwordVisibility.currentPassword
                              ? "text"
                              : "password"
                          }
                          className={`pass-input form-control ${
                            passwordErrors.currentPassword ? "is-invalid" : ""
                          }`}
                          placeholder="Enter current password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <span
                          className={`ti toggle-passwords ${
                            passwordVisibility.currentPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                          }`}
                          onClick={() =>
                            togglePasswordVisibility("currentPassword")
                          }
                        ></span>
                      </div>
                      {passwordErrors.currentPassword && (
                        <div className="text-danger mt-1">
                          {passwordErrors.currentPassword}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <div className="pass-group d-flex">
                        <input
                          type={
                            passwordVisibility.newPassword ? "text" : "password"
                          }
                          className={`pass-input form-control ${
                            passwordErrors.newPassword ? "is-invalid" : ""
                          }`}
                          placeholder="Enter new password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <span
                          className={`ti toggle-passwords ${
                            passwordVisibility.newPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                          }`}
                          onClick={() =>
                            togglePasswordVisibility("newPassword")
                          }
                        ></span>
                      </div>
                      {passwordErrors.newPassword && (
                        <div className="text-danger mt-1">
                          {passwordErrors.newPassword}
                        </div>
                      )}
                    </div>
                    <div className="mb-0">
                      <label className="form-label">Confirm Password</label>
                      <div className="pass-group d-flex">
                        <input
                          type={
                            passwordVisibility.confirmPassword
                              ? "text"
                              : "password"
                          }
                          className={`pass-input form-control ${
                            passwordErrors.confirmPassword ? "is-invalid" : ""
                          }`}
                          placeholder="Confirm new password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <span
                          className={`ti toggle-passwords ${
                            passwordVisibility.confirmPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                          }`}
                          onClick={() =>
                            togglePasswordVisibility("confirmPassword")
                          }
                        ></span>
                      </div>
                      {passwordErrors.confirmPassword && (
                        <div className="text-danger mt-1">
                          {passwordErrors.confirmPassword}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetPasswordForm}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating || !isPasswordValid}
                  id="modal_confirm_password"
                >
                  {updating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Changing...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </button>
                {/* Hidden button for programmatic closing */}
                <button
                  type="button"
                  id="change_password_modal_close"
                  data-bs-dismiss="modal"
                  style={{ display: "none" }}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Change Password Modal */}
    </div>
  )
}

export default Profile
